import type { ReactNode } from 'react';
import type { Verdict, Direction } from '../lib/types';
import { verdictColor } from '../lib/verdict';
import { formatScore, formatPct } from '../lib/format';
import { SpectrumGauge } from './SpectrumGauge';
import styles from './VerdictHeader.module.css';

interface Props {
  verdict: Verdict;
  score: number;
  confidence: number;
  summary: string;
  title: string;
  /** Editorial headline supplied by Pass 3. If absent, we synthesise one from
   *  the title + verdict (mechanical fallback). */
  headline?: string;
  direction: Direction;
  exportMenu: ReactNode;
  documentLabel?: string; // e.g. "Earnings Call · Q2 FY26"
  className?: string;
}

function editorialHeadline(title: string, verdict: Verdict): string {
  const verdictPhrase =
    verdict === 'BULLISH' ? 'bullish' : verdict === 'BEARISH' ? 'bearish' : 'mixed signals';
  // Drop a trailing common word for tighter sentence-case
  const trimmed = title.replace(/[—–-]+\s.*$/, '').trim();
  return `${trimmed}: ${verdictPhrase}.`;
}

export function VerdictHeader({
  verdict,
  score,
  confidence,
  summary,
  title,
  headline,
  direction,
  exportMenu,
  documentLabel,
  className,
}: Props) {
  const c = verdictColor(verdict);
  const finalHeadline = (headline && headline.trim()) || editorialHeadline(title, verdict);

  if (direction === 'C') {
    return (
      <section
        className={[styles.editorial, className].filter(Boolean).join(' ')}
        data-direction="C"
        aria-label="Verdict"
      >
        <div className={styles.editorialHeader}>
          <span className={styles.editorialEyebrow}>{documentLabel ?? 'Sentiment Verdict'}</span>
          <div className={styles.editorialActions}>{exportMenu}</div>
        </div>
        <h1 className={styles.editorialHeadline}>
          {finalHeadline}
        </h1>
        <p className={styles.editorialSummary}>{summary}</p>
        <div className={styles.editorialMeta}>
          <span className={styles.editorialMetaItem}>
            <span className={styles.metaLabel}>Verdict</span>
            <span className={styles.metaValue} style={{ color: c.fg }}>{verdict}</span>
          </span>
          <span className={styles.editorialMetaItem}>
            <span className={styles.metaLabel}>Score</span>
            <span className={[styles.metaValue, styles.mono].join(' ')}>{formatScore(score)}</span>
          </span>
          <span className={styles.editorialMetaItem}>
            <span className={styles.metaLabel}>Confidence</span>
            <span className={[styles.metaValue, styles.mono].join(' ')}>{formatPct(confidence)}</span>
          </span>
          {documentLabel && (
            <span className={styles.editorialMetaItem}>
              <span className={styles.metaLabel}>Document</span>
              <span className={styles.metaValue}>{documentLabel}</span>
            </span>
          )}
        </div>
        <div className={styles.editorialGauge}>
          <SpectrumGauge value={score} height={8} />
        </div>
      </section>
    );
  }

  if (direction === 'B') {
    return (
      <section
        className={[styles.terminal, className].filter(Boolean).join(' ')}
        data-direction="B"
        aria-label="Verdict"
      >
        <div className={styles.terminalRow}>
          <div className={styles.terminalLeft}>
            <span className={styles.terminalEyebrow}>{documentLabel ?? 'Sentiment Verdict'}</span>
            <h1 className={styles.terminalVerdict} style={{ color: c.accent }}>{verdict}</h1>
            <p className={styles.terminalSummary}>{summary}</p>
          </div>
          <div className={styles.terminalRight}>
            <div className={styles.terminalMetric}>
              <span className={styles.metaLabel}>Score</span>
              <span className={[styles.terminalNum, styles.mono].join(' ')}>{formatScore(score)}</span>
            </div>
            <div className={styles.terminalMetric}>
              <span className={styles.metaLabel}>Confidence</span>
              <span className={[styles.terminalNum, styles.mono].join(' ')}>{formatPct(confidence)}</span>
            </div>
            {exportMenu}
          </div>
        </div>
        <div className={styles.terminalGauge}>
          <SpectrumGauge value={score} />
        </div>
      </section>
    );
  }

  // Direction A — Research Note
  return (
    <section
      className={[styles.research, className].filter(Boolean).join(' ')}
      data-direction="A"
      aria-label="Verdict"
    >
      <div className={styles.researchRow}>
        <div className={styles.researchLeft}>
          <span className={styles.researchEyebrow}>{documentLabel ?? 'Sentiment Verdict'}</span>
          <h1 className={styles.researchVerdict} style={{ color: c.fg }}>{verdict}</h1>
          <p className={styles.researchSummary}>{summary}</p>
          <div className={styles.researchInline}>
            <span className={styles.researchInlineItem}>
              <span className={styles.metaLabel}>Score</span>
              <span className={[styles.researchNum, styles.mono].join(' ')}>{formatScore(score)}</span>
            </span>
            <span className={styles.researchInlineItem}>
              <span className={styles.metaLabel}>Confidence</span>
              <div className={styles.confidenceWrap}>
                <span className={[styles.researchNum, styles.mono].join(' ')}>{formatPct(confidence)}</span>
                <div className={styles.confidenceTrack}>
                  <div className={styles.confidenceFill} style={{ width: `${confidence}%` }} />
                </div>
              </div>
            </span>
          </div>
        </div>
        <div className={styles.researchRight}>{exportMenu}</div>
      </div>
      <div className={styles.researchGauge}>
        <SpectrumGauge value={score} />
      </div>
    </section>
  );
}
